import { Box } from '@chakra-ui/react';
import type { PointerEvent as ReactPointerEvent } from 'react';
import { MIN_TOUCH_TARGET_PX } from './layoutConstants';

interface ResizeHandleProps {
  onResizeStart: (event?: { pointerId: number }) => void;
  /** Total hit/target width in px (layout reserves this much space). */
  hitWidth?: number;
  /** Visible line thickness in px */
  lineWidth?: number;
  hideOnMobile?: boolean;
  /** Match sticky sidebar vertical extent */
  minHeightDesktop?: string;
  ariaLabel?: string;
}

export function ResizeHandle({
  onResizeStart,
  /** Total strip width for easier grabbing (layout offset uses this full width). */
  hitWidth = 6,
  /** Visible divider thickness inside the hit zone */
  lineWidth = 1,
  hideOnMobile = true,
  minHeightDesktop = 'calc(100vh - 88px)',
  ariaLabel = 'Resize sidebar',
}: ResizeHandleProps) {
  const handlePointerDown = (event: ReactPointerEvent<HTMLDivElement>) => {
    if (event.button !== 0) return;
    event.preventDefault();
    event.stopPropagation();
    try {
      event.currentTarget.setPointerCapture(event.pointerId);
    } catch {
      /* The pointer can end before capture is set. */
    }
    onResizeStart({ pointerId: event.pointerId });
  };

  return (
    <Box
      position="relative"
      zIndex={2}
      w={`${hitWidth}px`}
      flexShrink={0}
      display={hideOnMobile ? { base: 'none', md: 'flex' } : 'flex'}
      alignItems="stretch"
      justifyContent="center"
      cursor="col-resize"
      onPointerDown={handlePointerDown}
      minH={{ base: 0, md: minHeightDesktop }}
      role="separator"
      aria-orientation="vertical"
      aria-label={ariaLabel}
      userSelect="none"
      css={{
        touchAction: 'none',
        '@media (any-pointer: coarse)': {
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            bottom: 0,
            left: '50%',
            width: `${MIN_TOUCH_TARGET_PX}px`,
            transform: 'translateX(-50%)',
          },
        },
      }}
      _hover={{
        '& .rift-resize-line': {
          bg: 'accent',
          opacity: 1,
        },
      }}
      _active={{
        '& .rift-resize-line': {
          bg: 'accent',
          opacity: 1,
        },
      }}
    >
      <Box
        className="rift-resize-line"
        alignSelf="stretch"
        w={`${lineWidth}px`}
        maxW={`${lineWidth}px`}
        flexShrink={0}
        borderRadius="full"
        bg="border"
        opacity={0}
        transition="background 0.15s ease, opacity 0.15s ease"
      />
    </Box>
  );
}
